"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Engine = void 0;
const Types_1 = require("./Types");
const Rules_1 = require("./Rules");
const EventDispatcher_1 = require("./events/EventDispatcher");
const PRNG_1 = require("./utils/PRNG");
const GameEvent_1 = require("./events/GameEvent");
const StateValidation_1 = require("./StateValidation");
class Engine {
    static VERSION = "0.0.1";
    config;
    eventDispatcher = new EventDispatcher_1.EventDispatcher();
    currentState;
    prng;
    dispatching = false;
    constructor(config) {
        if (!Number.isSafeInteger(config.seed))
            throw new Error("seed must be a safe integer");
        if (config.dealerIndex !== undefined && !Types_1.PLAYERS.includes(config.dealerIndex)) {
            throw new Error("dealerIndex must be between 0 and 3");
        }
        this.config = Object.freeze({ ...config });
        this.init();
    }
    init(state) {
        if (this.dispatching)
            throw new Error("Cannot reset the engine during event delivery");
        if (state !== undefined) {
            const restored = structuredClone(state);
            (0, StateValidation_1.validateState)(restored, this.config);
            const prng = new PRNG_1.PRNG(this.config.seed);
            prng.restoreState(restored.prngState);
            this.currentState = restored;
            this.prng = prng;
            return;
        }
        this.prng = new PRNG_1.PRNG(this.config.seed);
        const dealer = this.config.dealerIndex ?? 0;
        this.currentState = {
            config: { ...this.config }, prngState: this.prng.getState(),
            phase: Types_1.Phase.READY, handNumber: 0, dealerIndex: dealer, currentPlayerIndex: dealer,
            hands: [[], [], [], []], kitty: [], upcard: null, biddingRound: 1, passes: 0,
            trump: null, makerIndex: null, alone: false, inactivePlayerIndex: null,
            currentTrick: [], completedTricks: [], tricks: [0, 0], scores: [0, 0],
            lastResult: null, winningTeam: null
        };
    }
    getState() {
        return structuredClone(this.currentState);
    }
    getObservation(playerIndex) {
        if (!Types_1.PLAYERS.includes(playerIndex))
            throw new Error("Unknown player");
        const { hands, kitty: _kitty, config: _config, prngState: _prngState, ...publicState } = this.getState();
        return {
            ...publicState, playerIndex, hand: hands[playerIndex],
            handSizes: hands.map(hand => hand.length), legalActions: this.getLegalActions(playerIndex)
        };
    }
    addEventListener(type, listener, scope) {
        this.eventDispatcher.addEventListener(type, listener, scope);
    }
    removeEventListener(type, listener, scope) {
        this.eventDispatcher.removeEventListener(type, listener, scope);
    }
    removeAllListeners() {
        this.eventDispatcher.removeAllEventListeners();
    }
    getLegalActions(playerIndex) {
        const s = this.currentState;
        if (playerIndex !== s.currentPlayerIndex)
            return [];
        if (s.phase === Types_1.Phase.READY || s.phase === Types_1.Phase.HAND_OVER)
            return [{ type: Types_1.ActionType.DEAL }];
        if (s.phase === Types_1.Phase.BIDDING) {
            const actions = [];
            if (s.biddingRound === 1 || playerIndex !== s.dealerIndex) {
                actions.push({ type: Types_1.ActionType.PASS, playerIndex });
            }
            for (const alone of [false, true]) {
                if (s.biddingRound === 1) {
                    actions.push({ type: Types_1.ActionType.ORDER_UP, playerIndex, alone });
                }
                else {
                    for (const suit of Types_1.SUITS.filter(suit => suit !== s.upcard?.suit)) {
                        actions.push({ type: Types_1.ActionType.CALL_TRUMP, playerIndex, suit, alone });
                    }
                }
            }
            return actions;
        }
        if (s.phase === Types_1.Phase.DISCARD) {
            return s.hands[playerIndex].map(card => ({ type: Types_1.ActionType.DISCARD, playerIndex, cardId: card.id }));
        }
        if (s.phase === Types_1.Phase.PLAYING && s.trump) {
            return (0, Rules_1.legalCards)(s.hands[playerIndex], s.currentTrick, s.trump)
                .map(card => ({ type: Types_1.ActionType.PLAY_CARD, playerIndex, cardId: card.id }));
        }
        return [];
    }
    processAction(action) {
        if (this.dispatching)
            throw new Error("Schedule actions after event delivery, not inside a listener");
        const events = [];
        if (!this.isLegal(action)) {
            this.deliver(action, [{
                    type: Types_1.EventType.ACTION_REJECTED,
                    message: `Action ${action?.type ?? "unknown"} is not legal in ${this.currentState.phase} for the current player`
                }]);
            return false;
        }
        const s = this.currentState;
        switch (action.type) {
            case Types_1.ActionType.DEAL:
                this.deal(events);
                break;
            case Types_1.ActionType.PASS:
                s.passes++;
                if (s.passes === 4) {
                    s.biddingRound = 2;
                    s.passes = 0;
                }
                s.currentPlayerIndex = (0, Rules_1.nextPlayer)(s.currentPlayerIndex);
                events.push({ type: Types_1.EventType.BID_MADE, playerIndex: action.playerIndex });
                break;
            case Types_1.ActionType.ORDER_UP:
            case Types_1.ActionType.CALL_TRUMP: {
                const trump = action.type === Types_1.ActionType.ORDER_UP ? s.upcard.suit : action.suit;
                s.trump = trump;
                s.makerIndex = action.playerIndex;
                s.alone = action.alone;
                s.inactivePlayerIndex = action.alone ? (0, Rules_1.partnerOf)(action.playerIndex) : null;
                events.push({ type: Types_1.EventType.BID_MADE, playerIndex: action.playerIndex });
                events.push({ type: Types_1.EventType.TRUMP_CHOSEN, playerIndex: action.playerIndex });
                if (action.type === Types_1.ActionType.ORDER_UP) {
                    s.hands[s.dealerIndex].push(s.kitty.shift());
                    s.phase = Types_1.Phase.DISCARD;
                    s.currentPlayerIndex = s.dealerIndex;
                }
                else {
                    this.startPlay();
                }
                break;
            }
            case Types_1.ActionType.DISCARD: {
                const hand = s.hands[action.playerIndex];
                const [card] = hand.splice(hand.findIndex(card => card.id === action.cardId), 1);
                s.kitty.push(card);
                this.startPlay();
                // Discards are private; only the acting player's observation reveals its hand.
                events.push({ type: Types_1.EventType.CARD_DISCARDED, playerIndex: action.playerIndex });
                break;
            }
            case Types_1.ActionType.PLAY_CARD:
                this.play(action, events);
                break;
        }
        if (s.phase !== Types_1.Phase.HAND_OVER && s.phase !== Types_1.Phase.GAME_OVER) {
            events.push({ type: Types_1.EventType.TURN_CHANGED, playerIndex: s.currentPlayerIndex });
        }
        this.deliver(action, events);
        return true;
    }
    isLegal(action) {
        if (!action || typeof action !== "object")
            return false;
        const player = action.type === Types_1.ActionType.DEAL ? this.currentState.currentPlayerIndex : action.playerIndex;
        return this.getLegalActions(player).some(legal => {
            if (legal.type !== action.type)
                return false;
            if (legal.type === Types_1.ActionType.DEAL)
                return true;
            if (legal.playerIndex !== player)
                return false;
            switch (action.type) {
                case Types_1.ActionType.PASS: return true;
                case Types_1.ActionType.ORDER_UP:
                    return legal.type === Types_1.ActionType.ORDER_UP && legal.alone === action.alone;
                case Types_1.ActionType.CALL_TRUMP:
                    return legal.type === Types_1.ActionType.CALL_TRUMP && legal.suit === action.suit && legal.alone === action.alone;
                case Types_1.ActionType.DISCARD:
                case Types_1.ActionType.PLAY_CARD:
                    return (legal.type === Types_1.ActionType.DISCARD || legal.type === Types_1.ActionType.PLAY_CARD)
                        && legal.cardId === action.cardId;
            }
        });
    }
    deal(events) {
        const s = this.currentState;
        if (s.phase === Types_1.Phase.HAND_OVER)
            s.dealerIndex = (0, Rules_1.nextPlayer)(s.dealerIndex);
        s.handNumber++;
        const deck = this.prng.shuffleArray((0, Rules_1.createDeck)());
        s.prngState = this.prng.getState();
        s.hands = [[], [], [], []];
        let player = (0, Rules_1.nextPlayer)(s.dealerIndex);
        for (const packetSize of [3, 2]) {
            for (let i = 0; i < 4; i++) {
                s.hands[player].push(...deck.splice(0, packetSize));
                player = (0, Rules_1.nextPlayer)(player);
            }
        }
        s.kitty = deck;
        s.upcard = deck[0];
        s.biddingRound = 1;
        s.passes = 0;
        s.trump = null;
        s.makerIndex = null;
        s.alone = false;
        s.inactivePlayerIndex = null;
        s.currentTrick = [];
        s.completedTricks = [];
        s.tricks = [0, 0];
        s.lastResult = null;
        s.phase = Types_1.Phase.BIDDING;
        s.currentPlayerIndex = (0, Rules_1.nextPlayer)(s.dealerIndex);
        events.push({ type: Types_1.EventType.DEAL_EXECUTED });
    }
    nextActive(player) {
        const next = (0, Rules_1.nextPlayer)(player);
        return next === this.currentState.inactivePlayerIndex ? (0, Rules_1.nextPlayer)(next) : next;
    }
    startPlay() {
        this.currentState.phase = Types_1.Phase.PLAYING;
        this.currentState.currentPlayerIndex = this.nextActive(this.currentState.dealerIndex);
    }
    play(action, events) {
        const s = this.currentState;
        const hand = s.hands[action.playerIndex];
        const [card] = hand.splice(hand.findIndex(card => card.id === action.cardId), 1);
        s.currentTrick.push({ playerIndex: action.playerIndex, card });
        events.push({ type: Types_1.EventType.CARD_PLAYED, playerIndex: action.playerIndex, card });
        if (s.currentTrick.length < (s.alone ? 3 : 4)) {
            s.currentPlayerIndex = this.nextActive(action.playerIndex);
            return;
        }
        const winnerIndex = (0, Rules_1.trickWinner)(s.currentTrick, s.trump);
        const trick = { plays: s.currentTrick, winnerIndex };
        s.completedTricks.push(trick);
        s.currentTrick = [];
        s.tricks[(0, Rules_1.teamOf)(winnerIndex)]++;
        s.currentPlayerIndex = winnerIndex;
        events.push({ type: Types_1.EventType.TRICK_COMPLETED, trick });
        if (s.completedTricks.length === 5) {
            const result = (0, Rules_1.scoreHand)(s.makerIndex, s.tricks, s.alone);
            s.lastResult = result;
            s.scores[result.winningTeam] += result.points;
            s.phase = s.scores[result.winningTeam] >= 10 ? Types_1.Phase.GAME_OVER : Types_1.Phase.HAND_OVER;
            s.winningTeam = s.phase === Types_1.Phase.GAME_OVER ? result.winningTeam : null;
            events.push({ type: Types_1.EventType.HAND_COMPLETED, result });
            if (s.phase === Types_1.Phase.GAME_OVER)
                events.push({ type: Types_1.EventType.GAME_OVER, result });
        }
    }
    deliver(action, events) {
        this.dispatching = true;
        try {
            for (const event of events) {
                const params = structuredClone({ ...event, action, state: this.currentState });
                this.eventDispatcher.dispatchEvent(new GameEvent_1.GameEvent(event.type, params));
            }
        }
        finally {
            this.dispatching = false;
        }
    }
}
exports.Engine = Engine;
